import type { AppBranding } from "@/app/theme";
import { buildAppIcons, type AppIconSet } from "./icons";
import { DEFAULT_PRIMARY } from "@/app/branding";
import { VENDOR_NAME } from "@/app/vendor";

/**
 * Per-tenant web app manifest, built at runtime.
 *
 * WHY NOT A STATIC FILE: one build of this shell is served from ONE bucket
 * to EVERY tenant distribution (D29) — the Desk and every customer's
 * white-label domain download byte-identical files. A
 * `dist/manifest.webmanifest` could therefore only ever carry one app's name
 * and icon, and a white-label app must never show the vendor's mark. The
 * manifest is instead composed from the resolved tenant and injected as a
 * same-origin `blob:` URL.
 *
 * CONSEQUENCE — every URL in the manifest must be ABSOLUTE. Relative URLs
 * resolve against the MANIFEST's own URL, which here is `blob:https://host/uuid`;
 * `start_url: "/"` against that base is not the site root. `start_url`, `scope`
 * and `id` are therefore written out in full from `window.location.origin`, and
 * the icons are `data:` URLs (see ./icons.ts).
 *
 * WHAT IT NEEDS FROM THE TENANT ROW, exactly — recorded here as a D115 Stage 3
 * input, because a delivery model that changes where bundles come from must not
 * accidentally change what an installed app is. `boot.ts` passes four things
 * out of the resolved `tenantApps` row, and all four decide only how the app
 * PRESENTS:
 *   - `kind` and `branding.identity` — whether it stands under its own name or
 *     wears the vendor's (`appDisplayName`);
 *   - `name` — the installed app's name and short name;
 *   - `branding.colours` — `navbar` becomes `theme_color`, `background` becomes
 *     `background_color`, `primary` tints a generated icon;
 *   - `branding.iconUrl` (or `logoLightUrl`) — the source image for the icons.
 * With no usable tenant the shell never calls this at all, so the app is simply
 * not installable; there is no half-branded manifest to worry about.
 *
 * 🔴 The row decides NONE of `id`, `start_url` or `scope`. All three are
 * `${origin}/`, and `id` is what a browser keys an installation on — so no
 * tenant edit, and nothing D115 changes about how bundles are addressed, can
 * orphan an already-installed app. Anything that WOULD move `id` is a
 * user-visible reinstall, not a refactor.
 */

export interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: string;
}

export interface WebAppManifest {
  id: string;
  name: string;
  short_name: string;
  description?: string;
  start_url: string;
  scope: string;
  display: string;
  display_override: string[];
  orientation: string;
  theme_color: string;
  background_color: string;
  icons: ManifestIcon[];
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

function safeColour(value: string | null | undefined, fallback: string): string {
  return value && HEX_COLOUR.test(value) ? value : fallback;
}

/**
 * The installed app's name — the app's own, because it stands under its own identity (D118).
 *
 * It used to be `hasOwnIdentity(kind, branding) ? name : "Pinksy " + name`: a WHITE_LABEL app, and a
 * PRODUCT app whose branding said `identity: "own"`, stood under its own name, and everything else
 * wore the vendor's with the product as qualifier. Pinksy is the product and its branding says
 * `own`, so the false branch was unreachable for the only app that ever existed.
 */
export function appDisplayName(name: string): string {
  return name.trim() || VENDOR_NAME;
}

export function buildManifest(options: {
  name: string;
  origin: string;
  branding: AppBranding | null;
  icons: AppIconSet | null;
}): WebAppManifest {
  const { name, origin, branding, icons } = options;
  const dark = branding?.defaultTheme !== "light";
  const themeColour = safeColour(branding?.colours?.navbar, dark ? "#1D2126" : "#2B3037");
  const backgroundColour = safeColour(branding?.colours?.background, dark ? "#131619" : "#E9E7E2");
  const start = `${origin}/`;

  return {
    // 🔴 Stable app identity, and the one field in here that a person would
    // feel if it moved: a browser keys an INSTALLATION on `id`, so changing it
    // does not update the installed app, it strands it beside a second one.
    // Pinned to the origin rather than left to default to `start_url`, so a
    // later change to the landing route cannot do that by accident.
    id: start,
    name: appDisplayName(name),
    short_name: name,
    start_url: start,
    scope: start,
    display: "standalone",
    display_override: ["standalone", "minimal-ui"],
    orientation: "any",
    theme_color: themeColour,
    background_color: backgroundColour,
    icons: icons
      ? [
          { src: icons.any192, sizes: "192x192", type: "image/png", purpose: "any" },
          { src: icons.any512, sizes: "512x512", type: "image/png", purpose: "any" },
          { src: icons.maskable512, sizes: "512x512", type: "image/png", purpose: "maskable" },
        ]
      : [],
  };
}

let currentManifestUrl: string | null = null;

function upsertLink(rel: string, href: string, attrs: Record<string, string> = {}): void {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value);
  el.href = href;
}

function upsertMeta(name: string, content: string): void {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Publish the manifest as a same-origin blob and point `<link rel="manifest">` at it. */
export function injectManifest(manifest: WebAppManifest): string {
  const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
  const url = URL.createObjectURL(blob);
  if (currentManifestUrl) URL.revokeObjectURL(currentManifestUrl);
  currentManifestUrl = url;
  upsertLink("manifest", url);
  return url;
}

/**
 * Brand the app for installation: manifest, plus the iOS tags — Safari reads
 * `apple-touch-icon` and `apple-mobile-web-app-title` for "Add to Home
 * Screen" instead of the manifest's icons and name.
 *
 * Best-effort by design: it is called after mount and every failure inside is
 * swallowed, because a browser that cannot install the app must still run it.
 */
export async function applyPwaManifest(options: {
  name: string;
  branding: AppBranding | null;
  origin?: string;
}): Promise<WebAppManifest | null> {
  try {
    const origin = options.origin ?? window.location.origin;
    const icons = await buildAppIcons(
      appDisplayName(options.name),
      options.branding?.iconUrl || options.branding?.logoLightUrl || null,
      options.branding?.colours?.primary || DEFAULT_PRIMARY,
    );
    const manifest = buildManifest({ ...options, origin, icons });
    injectManifest(manifest);

    if (icons) upsertLink("apple-touch-icon", icons.any192, { sizes: "192x192" });
    upsertMeta("apple-mobile-web-app-title", manifest.short_name);
    upsertMeta("application-name", manifest.short_name);
    return manifest;
  } catch {
    return null;
  }
}
